import { FormEvent, useCallback, useEffect, useState } from 'react'
import ConfirmDialog from '../ConfirmDialog'
import Spinner from '../Spinner'
import { libraryAPI } from '../../services/api'
import type { LibraryAnswer, LibraryNote } from '../../types'
import { apiErrorMessage } from '../../utils/apiError'

const KINDS: { id: string; label: string }[] = [
  { id: 'market_analysis', label: 'Market analysis' },
  { id: 'research_report', label: 'Research' },
  { id: 'creator_content', label: 'My writing (your voice)' },
  { id: 'trading_course', label: 'Course notes' },
  { id: 'news_article', label: 'Article' },
  { id: 'whitepaper', label: 'Whitepaper' },
]
const kindLabel = (id: string) => KINDS.find((k) => k.id === id)?.label ?? id.replace(/_/g, ' ')

/** Ask a question; the answer is drawn only from the creator's own notes. */
const Ask = ({ hasNotes }: { hasNotes: boolean }) => {
  const [question, setQuestion] = useState('')
  const [asking, setAsking] = useState(false)
  const [result, setResult] = useState<LibraryAnswer | null>(null)
  const [error, setError] = useState<string | null>(null)

  const ask = async (e: FormEvent) => {
    e.preventDefault()
    setAsking(true)
    setError(null)
    setResult(null)
    try {
      setResult(await libraryAPI.ask(question.trim()))
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't answer that."))
    } finally {
      setAsking(false)
    }
  }

  return (
    <section className="rounded-lg bg-white p-4 shadow" aria-label="Ask your notes">
      <h2 className="text-lg font-semibold text-gray-900">Ask your notes</h2>
      <form onSubmit={ask} className="mt-2 flex flex-wrap gap-2">
        <input
          aria-label="Question"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="e.g. What did I say about sizing around funding flips?"
          className="min-w-0 flex-1 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
        />
        <button
          type="submit"
          disabled={asking || !hasNotes || question.trim().length < 3}
          className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {asking ? 'Searching…' : 'Ask'}
        </button>
      </form>
      {error && (
        <p role="alert" className="mt-2 text-sm text-danger-700">
          {error}
        </p>
      )}
      {result && (
        <div className="mt-3 space-y-3 text-sm" data-testid="notes-answer">
          {result.answer ? (
            <p className="whitespace-pre-line text-gray-800">{result.answer}</p>
          ) : (
            <p className="text-gray-600">{result.unavailable ?? 'Nothing in your notes matches that question.'}</p>
          )}
          {result.sources.length > 0 && (
            <div>
              <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500">From your notes</h3>
              <ul className="mt-1 space-y-2">
                {result.sources.map((s) => (
                  <li key={s.document_id}>
                    <span className="font-medium text-gray-900">{s.title}</span>
                    <p className="text-gray-600">{s.excerpt}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  )
}

/**
 * Content → Notes: the creator's own research, course notes and analysis,
 * searchable by question. Answers come only from these notes, with the
 * passages they used; nothing is drawn from elsewhere.
 */
const NotesTab = () => {
  const [notes, setNotes] = useState<LibraryNote[] | null>(null)
  const [title, setTitle] = useState('')
  const [kind, setKind] = useState(KINDS[0].id)
  const [text, setText] = useState('')
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState<LibraryNote | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    libraryAPI
      .list()
      .then((r) => setNotes(r.documents))
      .catch((e) => setError(apiErrorMessage(e, "Couldn't load your notes.")))
  }, [])
  useEffect(load, [load])

  const add = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      await libraryAPI.add({ title: title.trim(), content: text, document_type: kind })
      setTitle('')
      setText('')
      load()
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't save the note."))
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    if (!deleting) return
    try {
      await libraryAPI.remove(deleting.id)
      setDeleting(null)
      load()
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't delete it."))
    }
  }

  if (!notes && !error) return <Spinner size="sm" />

  return (
    <div className="space-y-6">
      <Ask hasNotes={(notes ?? []).length > 0} />

      <section className="rounded-lg bg-white p-4 shadow" aria-label="Add a note">
        <h2 className="text-lg font-semibold text-gray-900">Add a note</h2>
        <p className="mt-1 text-sm text-gray-600" data-testid="voice-hint">
          Save a few posts you wrote yourself as &ldquo;My writing&rdquo;: new drafts take their tone and phrasing (never
          their facts or figures).
        </p>
        <form onSubmit={add} className="mt-2 space-y-2">
          <div className="flex flex-wrap gap-2">
            <input
              aria-label="Note title"
              placeholder="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={500}
              className="min-w-0 flex-1 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
            />
            <select
              aria-label="Kind"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"
            >
              {KINDS.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.label}
                </option>
              ))}
            </select>
          </div>
          <textarea
            aria-label="Note text"
            placeholder="Paste or write the note"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
            className="w-full rounded-md border border-gray-300 p-2 text-sm"
          />
          <button
            type="submit"
            disabled={saving || !title.trim() || !text.trim()}
            className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save note'}
          </button>
        </form>
      </section>

      {error && (
        <p role="alert" className="text-sm text-danger-700">
          {error}
        </p>
      )}

      {notes && notes.length > 0 && (
        <section className="rounded-lg bg-white p-4 shadow" aria-label="Your notes">
          <h2 className="text-lg font-semibold text-gray-900">Your notes ({notes.length})</h2>
          <ul className="mt-2 divide-y divide-gray-100 text-sm">
            {notes.map((n) => (
              <li key={n.id} className="py-2" data-testid="note">
                <div className="flex flex-wrap items-center gap-x-3">
                  <span className="font-medium text-gray-900">{n.title}</span>
                  <span className="text-xs text-gray-500">
                    {kindLabel(n.document_type)} · {new Date(n.created_at).toLocaleDateString()}
                  </span>
                  {n.processing_status !== 'indexed' && (
                    <span className="text-xs text-amber-800">
                      {n.processing_status === 'failed' ? "Couldn't be read: not searchable" : 'Not searchable yet'}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => setDeleting(n)}
                    className="ml-auto text-xs text-gray-500 hover:underline"
                    aria-label={`Delete ${n.title}`}
                  >
                    Delete
                  </button>
                </div>
                {n.summary && <p className="mt-0.5 text-gray-600">{n.summary}</p>}
              </li>
            ))}
          </ul>
        </section>
      )}

      <ConfirmDialog
        isOpen={deleting !== null}
        title="Delete this note?"
        message={deleting ? `"${deleting.title}" is removed from your notes.` : ''}
        confirmLabel="Delete"
        danger
        onCancel={() => setDeleting(null)}
        onConfirm={remove}
      />
    </div>
  )
}

export default NotesTab
